import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { saudeService } from '../services/api';

export interface Unidade {
  codunidade: string;
  nome: string;
  codcnes: string;
}

interface UnidadeContextType {
  /** Unidade selecionada (null = todas as unidades, comportamento atual) */
  unidadeSelecionada: Unidade | null;
  setUnidadeSelecionada: (u: Unidade | null) => void;
  /** Lista de unidades do municipio ativo (codcnes > 0) */
  unidades: Unidade[];
  loading: boolean;
}

const UnidadeContext = createContext<UnidadeContextType | undefined>(undefined);

const STORAGE_KEY = 'saude_unidade_selecionada';

export const UnidadeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { municipioAtivo } = useAuth();
  const [unidades, setUnidades] = useState<Unidade[]>([]);
  const [loading, setLoading] = useState(false);
  const [unidadeSelecionada, setUnidadeSelecionadaState] = useState<Unidade | null>(null);

  const setUnidadeSelecionada = useCallback((u: Unidade | null) => {
    setUnidadeSelecionadaState(u);
    try {
      if (u && municipioAtivo) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ municipioId: municipioAtivo.id, unidade: u }));
      } else {
        localStorage.removeItem(STORAGE_KEY);
      }
    } catch {
      /* ignore storage errors */
    }
  }, [municipioAtivo]);

  // Ao trocar de municipio: zera a selecao e recarrega a lista de unidades
  useEffect(() => {
    if (!municipioAtivo?.id) {
      setUnidades([]);
      setUnidadeSelecionadaState(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setUnidadeSelecionadaState(null);

    saudeService
      .getUnidades(municipioAtivo.id)
      .then((data: Unidade[]) => {
        if (cancelled) return;
        const lista = Array.isArray(data) ? data : [];
        setUnidades(lista);
        // Restaura selecao persistida se ainda valida para este municipio
        try {
          const raw = localStorage.getItem(STORAGE_KEY);
          if (raw) {
            const saved = JSON.parse(raw);
            if (saved?.municipioId === municipioAtivo.id && saved?.unidade?.codunidade) {
              const match = lista.find((u) => u.codunidade === saved.unidade.codunidade);
              if (match) setUnidadeSelecionadaState(match);
              else localStorage.removeItem(STORAGE_KEY);
            }
          }
        } catch {
          /* ignore */
        }
      })
      .catch(() => {
        if (!cancelled) setUnidades([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [municipioAtivo?.id]);

  return (
    <UnidadeContext.Provider value={{ unidadeSelecionada, setUnidadeSelecionada, unidades, loading }}>
      {children}
    </UnidadeContext.Provider>
  );
};

export const useUnidade = () => {
  const ctx = useContext(UnidadeContext);
  if (!ctx) throw new Error('useUnidade deve ser usado dentro de um UnidadeProvider');
  return ctx;
};
